import { useState } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { Zap, Globe, Brain, Layers, Shield, Cpu, ArrowRight, Network, Eye, Sparkles } from 'lucide-react';

const FORECASTS = [
  { icon: Brain, phase: 'Now', title: 'Structured Execution', items: [
    'Multi-agent coordination across departments',
    'Memory-powered institutional knowledge',
    'Approval-based governance and audit trails',
    'Executive briefing and priority structuring',
    'Cross-entity operations with separation',
  ]},
  { icon: Network, phase: 'Emerging', title: 'Adaptive Intelligence', items: [
    'Self-optimizing workflow routing',
    'Predictive task prioritization',
    'Cross-agent learning from operational patterns',
    'Dynamic resource allocation across teams',
    'Intelligent escalation and anomaly surfacing',
  ]},
  { icon: Sparkles, phase: 'Horizon', title: 'Autonomous Operations', items: [
    'End-to-end process orchestration',
    'Strategic scenario modeling and analysis',
    'Real-time organizational intelligence synthesis',
    'Proactive risk identification and mitigation',
    'Self-healing workflow architectures',
  ]},
];

const CAPABILITIES = [
  { icon: Cpu, title: 'Process Orchestration', desc: 'Agents coordinate multi-step workflows across departments, routing tasks intelligently and managing handoffs without human intervention for routine operations.' },
  { icon: Eye, title: 'Organizational Intelligence', desc: 'Continuous synthesis of operational data into actionable intelligence — surfacing patterns, anomalies, and opportunities that manual processes miss.' },
  { icon: Globe, title: 'Global Coordination', desc: 'Multi-timezone, multi-language, multi-entity operations managed by agents aware of regional regulations, cultural contexts, and time-sensitive priorities.' },
  { icon: Shield, title: 'Governance at Scale', desc: 'As agent deployments grow, governance scales proportionally — role-based controls, approval chains, and audit systems maintain oversight without creating bottlenecks.' },
  { icon: Layers, title: 'Compound Memory', desc: 'Institutional memory that compounds over time. Every interaction, decision, and outcome strengthens the organizational knowledge base available to all authorized agents.' },
  { icon: Zap, title: 'Operational Leverage', desc: 'Multiply team output without linear headcount growth. Agents handle preparation, coordination, and follow-up — freeing human teams for strategic and creative work.' },
];

export default function ForecastSection() {
  const [activePhase, setActivePhase] = useState(0);

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="forecast-section">
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-50" />
      <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-aurocle-gold/4 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="overline mb-4">What Becomes Possible</div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
            The future of agent architecture.
          </h2>
          <p className="text-base text-aurocle-platinum-muted max-w-2xl mb-16">
            Multi-agent workforce architecture is not static. As agents learn from organizational patterns, the operational leverage compounds — transforming what's possible for serious businesses.
          </p>
        </ScrollReveal>

        {/* Phase selector */}
        <ScrollReveal delay={100}>
          <div className="flex gap-4 mb-12">
            {FORECASTS.map((f, i) => {
              const Icon = f.icon;
              return (
                <button key={i} onClick={() => setActivePhase(i)} data-testid={`phase-${f.phase.toLowerCase()}`}
                  className={`flex-1 p-6 text-left transition-all border ${
                    activePhase === i
                      ? 'bg-aurocle-gold/8 border-aurocle-gold/25 shadow-[0_0_30px_rgba(212,175,55,0.08)]'
                      : 'bg-aurocle-surface border-white/5 hover:border-white/10'
                  }`}>
                  <Icon size={20} className={activePhase === i ? 'text-aurocle-gold mb-3' : 'text-aurocle-text-secondary mb-3'} />
                  <div className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60 mb-1">{f.phase}</div>
                  <div className="font-heading text-base font-medium text-aurocle-text">{f.title}</div>
                </button>
              );
            })}
          </div>
        </ScrollReveal>

        <ScrollReveal delay={200}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-24">
            {FORECASTS[activePhase].items.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-aurocle-surface/50 border border-white/4 hover:border-aurocle-gold/15 transition-all group"
                style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-1.5 h-1.5 rounded-full bg-aurocle-gold/40 group-hover:bg-aurocle-gold transition-colors shrink-0" />
                <span className="text-sm text-aurocle-text">{item}</span>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Capabilities deep dive */}
        <ScrollReveal>
          <div className="overline mb-4">Agent Capabilities Deep Dive</div>
          <h3 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-aurocle-text mb-12">
            What agents can truly accomplish.
          </h3>
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CAPABILITIES.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <ScrollReveal key={i} delay={i * 60}>
                <div className="premium-card p-6 h-full group" data-testid={`capability-deep-${i}`}>
                  <div className="relative z-10">
                    <div className="w-12 h-12 flex items-center justify-center border border-aurocle-gold/15 mb-4 group-hover:border-aurocle-gold/40 group-hover:shadow-[0_0_20px_rgba(212,175,55,0.1)] transition-all">
                      <Icon size={20} className="text-aurocle-platinum-muted group-hover:text-aurocle-gold transition-colors" />
                    </div>
                    <h4 className="font-heading text-base font-medium text-aurocle-text mb-2">{cap.title}</h4>
                    <p className="text-xs text-aurocle-platinum-muted leading-relaxed">{cap.desc}</p>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay={400}>
          <div className="mt-12 text-center">
            <a href="#contact" className="btn-gold cta-burning" data-testid="forecast-cta">
              Explore What's Possible <ArrowRight size={14} />
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
